import type { Cart_jsonld_read } from '@/features/cart/cart-post-type';
import type { CartItem } from '@/features/cart/service/use-cart';

import React from 'react';

import CounterDesktop from '@/core/components/counter/desktop/counter-desktop';
import { Button, Modal } from '@/core/features';
import { getFormatedDateShort } from '@/core/features/date/date';
import { centsToPriceString } from '@/core/features/price/price';
import CheckoutCancellationInfoDesktop from '@/features/checkout/checkout-shared-components/checkout-cancellation-info-desktop';

import styles from './cart-overlay-desktop.module.scss';

type CartOverlayProps = {
    bookingRedirectUrl: string;
    cartItems: CartItem[];
    changeItemCount: (index: number, delta: number) => void;
    isLoading: boolean;
    onClose: () => void;
    schoolInfo: Cart_jsonld_read['schoolInfo'];
    totalPriceString: string;
};

export default function CartOverlayDesktop({
    bookingRedirectUrl,
    cartItems,
    changeItemCount,
    isLoading,
    onClose,
    schoolInfo,
    totalPriceString,
}: CartOverlayProps) {
    return (
        <>
            <Modal
                hasSmallOverlay={true}
                maxWidth={835}
                onClose={onClose}
                padding={'padding-20'}
            >
                <div className={styles.header}>
                    <div className={styles.productHeader}>
                        <div className={'font-size-18 bold'}>Warenkorb</div>
                        <div className={styles.courseName}>{schoolInfo.headline}</div>
                        <div className={`${styles.schoolName} line-clamp-1`}>{schoolInfo.subHeadline}</div>
                    </div>

                    <div className={styles.ctaLargeOverlay}>
                        <Button
                            fontSize={18}
                            fontWeight={'bold'}
                            href={bookingRedirectUrl}
                            isLoading={isLoading}
                            linkType={'vanilla'}
                            qaId={'qa-cart-overlay-checkout-large-button'}
                            title={`zur Buchung (${totalPriceString})`}
                            width={300}
                        />
                    </div>
                </div>

                <div className={styles.itemsList}>
                    {cartItems.map(
                        ({ cancellation, count, endDate, offerId, productName, startDate, unitPrice }, index) => {
                            const localizedStartDate = getFormatedDateShort(startDate);
                            const localizedEndDate = getFormatedDateShort(endDate);

                            const itemPrice = centsToPriceString(unitPrice * count.current);

                            return (
                                <div
                                    className={styles.item}
                                    key={`${offerId}-${index}`}
                                >
                                    <div className={styles.leftContainer}>
                                        <div className={'font-size-16 bold'}>{productName}</div>
                                        <div className={'font-size-16'}>
                                            {localizedStartDate} - {localizedEndDate}
                                        </div>
                                        {cancellation && (
                                            <div className={styles.cancellationMainWrapper}>
                                                <CheckoutCancellationInfoDesktop
                                                    fontSize={'font-size-14'}
                                                    iconUrl={cancellation.iconUrl}
                                                    overlayHtmlText={cancellation.overlay?.htmlText}
                                                    overlayHtmlTitle={cancellation.overlay?.title}
                                                    text={cancellation.text}
                                                    textColor={cancellation.color}
                                                    tippyConfig={{ placement: 'auto' }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.rightContainer}>
                                        <div className={'font-size-16'}>{itemPrice}</div>
                                        <CounterDesktop
                                            count={count.current}
                                            keyboardNavigationContainerId={'cart-overlay-desktop-counter'}
                                            max={count.max}
                                            min={count.min}
                                            onDecrease={() => changeItemCount(index, -1)}
                                            onIncrease={() => changeItemCount(index, 1)}
                                        />
                                    </div>
                                </div>
                            );
                        },
                    )}
                </div>
                <div className={`flex-space-between-horizontal ${styles.totalPriceWrapper}`}>
                    <div>
                        <span className={styles.priceLabel}>Gesamtpreis</span>
                        <span className={'font-size-16 bold flex-1'}>{totalPriceString}</span>
                    </div>
                </div>

                <div className={styles.ctaSmallOverlay}>
                    <Button
                        className={styles.ctaButtonSmallOverlay}
                        disablePrefetch={true}
                        fontSize={16}
                        fontWeight={'bold'}
                        href={bookingRedirectUrl}
                        isLoading={isLoading}
                        linkType={'internal'}
                        qaId={'qa-cart-overlay-checkout-small-button'}
                        title={`zur Buchung (${totalPriceString})`}
                        width={300}
                    />
                </div>
            </Modal>
        </>
    );
}
